import { ClipboardEvent, DragEvent, ReactNode, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Drawer,
  FileButton,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea
} from '@mantine/core'
import { IconArrowBackUp, IconLink, IconPhotoPlus, IconRefresh, IconX } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { usePasteResult } from 'hooks/desk/usePasteResult'
import { usePasteToDesk } from 'hooks/desk/usePasteToDesk'
import { useAppTranslation } from 'hooks/useTranslation'
import type { RestoreRawItemParams } from 'supabase/desk/restoreRawItem'
import type { PasteCandidate } from 'supabase/desk/queryPasteResult'
import { dateLocaleOf } from './compareRows'
import { formatStarts, SourceBadge } from './DeskTable'
import { publicImageUrl } from './deskImages'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_TEXT_CHARS,
  NotEventState,
  PASTE_READING_TIMEOUT_MS,
  PanelState,
  PasteAction,
  PasteSession,
  ResultState,
  canSend,
  detectPasteLink,
  initialPasteSession,
  isPasteInFlight,
  pasteRawItemId,
  pasteReducer,
  routeBadge,
  showsForm
} from './pastePanelState'
import { showDeskError } from './RejectMenu'

const THUMB = 64

/** The reading start the 120 s timer runs from, or null when nothing is being read. */
const readingStartedAt = (panel: PanelState): number | null => {
  if (panel.kind === 'reading') return panel.startedAt
  if (panel.kind === 'already' && panel.inner?.kind === 'reading') return panel.inner.startedAt
  return null
}

type UsePasteSessionParams = {
  /** useDeskDecision().restoreRawItem.mutateAsync: the D-12 raw-item restore. */
  restoreRawItem: (params: RestoreRawItemParams) => Promise<number>
}

/**
 * One paste session, owned by the Desk page (U-07): the form, the panel
 * state, the desk-paste calls, the 3 s poll and the 120 s timer. It keeps
 * running while the drawer is closed, so a paste is never lost by closing it,
 * and the header button can show that one is in flight.
 */
export const usePasteSession = ({ restoreRawItem }: UsePasteSessionParams) => {
  const { t } = useAppTranslation()
  const [session, dispatch] = useReducer(pasteReducer, initialPasteSession)
  const { send, retry } = usePasteToDesk()
  const rawItemId = pasteRawItemId(session.panel)
  const result = usePasteResult(rawItemId)
  const [restoring, setRestoring] = useState(false)

  // Every poll answer (a new read, or a refetch after a restore) moves the panel.
  const { data, dataUpdatedAt } = result
  useEffect(() => {
    if (data) dispatch({ type: 'poll', result: data, now: Date.now() })
  }, [data, dataUpdatedAt])

  // The reading timeout runs on its own timer: a poll that returns the same
  // `new` row changes nothing React can see, so it cannot carry the timeout.
  const startedAt = readingStartedAt(session.panel)
  useEffect(() => {
    if (startedAt === null) return undefined
    const timer = setTimeout(
      () => dispatch({ type: 'tick', now: Date.now() }),
      Math.max(0, startedAt + PASTE_READING_TIMEOUT_MS - Date.now())
    )
    return () => clearTimeout(timer)
  }, [startedAt])

  const submit = async () => {
    if (session.panel.kind === 'submitting' || !canSend(session.form)) return
    dispatch({ type: 'submit' })
    // The panel describes the text that was sent, not whatever is in the box when desk-paste answers.
    const sentText = session.form.text
    const outcome = await send
      .mutateAsync({ text: sentText, images: session.form.images })
      .catch(() => ({ ok: false as const, code: 'network', cap: null, status: null }))
    dispatch({ type: 'response', outcome, now: Date.now(), sentText })
  }

  const retryPaste = async (id: number) => {
    if (retry.isPending) return
    const outcome = await retry
      .mutateAsync(id)
      .catch(() => ({ ok: false as const, code: 'network', cap: null, status: null }))
    // No sentText: the box may hold a different paste, so a login_wall keeps the login wording.
    dispatch({ type: 'response', outcome, now: Date.now() })
  }

  const restore = async (panel: NotEventState) => {
    if (restoring) return
    setRestoring(true)
    try {
      await restoreRawItem({
        rawItemId: panel.rawItemId,
        mainPicture: panel.imagePath ? publicImageUrl(panel.imagePath) : null
      })
      // The restored candidate shows as in done once the raw item is re-read.
      await result.refetch()
    } catch (error) {
      showDeskError(t, error)
    } finally {
      setRestoring(false)
    }
  }

  return {
    session,
    dispatch,
    submit,
    retry: retryPaste,
    retrying: retry.isPending,
    restore,
    restoring,
    inFlight: isPasteInFlight(session.panel)
  }
}

export type PasteSessionControls = ReturnType<typeof usePasteSession>

/** Object URLs for the chosen images, revoked when the list changes or the drawer closes. */
const useThumbnailUrls = (images: File[]) => {
  const urls = useMemo(
    () => images.map((image) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(image) : '')),
    [images]
  )
  useEffect(
    () => () => {
      for (const url of urls) if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
    },
    [urls]
  )
  return urls
}

const imageFiles = (list: FileList | File[] | null | undefined): File[] =>
  Array.from(list ?? []).filter((file) => file.type.startsWith('image/'))

type FormProps = {
  session: PasteSession
  dispatch: (action: PasteAction) => void
  onSubmit: () => void
}

/** The idle form (UI-SPEC §G): text or a link, plus up to five images. */
const PasteForm = ({ session, dispatch, onSubmit }: FormProps) => {
  const { t } = useAppTranslation()
  const { form, panel } = session
  const submitting = panel.kind === 'submitting'
  const [blurred, setBlurred] = useState(false)
  const [dragging, setDragging] = useState(false)
  const resetFileInput = useRef<() => void>(null)
  const urls = useThumbnailUrls(form.images)

  const trimmed = form.text.trim()
  const link = detectPasteLink(form.text)
  const nothing = trimmed.length === 0 && form.images.length === 0
  const serverFieldError = panel.kind === 'idle' ? panel.fieldError ?? null : null
  const imageFieldError =
    serverFieldError === 'paste.too_many_images' || serverFieldError === 'paste.too_big' || serverFieldError === 'paste.wrong_type'
      ? serverFieldError
      : null

  const textError = (): string | null => {
    if (link === 'link_refused') return t('paste.link_refused')
    if (trimmed.length > MAX_TEXT_CHARS || serverFieldError === 'paste.text_too_long') return t('paste.text_too_long')
    if (nothing && (blurred || serverFieldError === 'paste.empty')) return t('paste.empty')
    return null
  }

  const addFiles = (files: File[]) => {
    if (submitting || files.length === 0) return
    dispatch({ type: 'add_images', files })
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!submitting) setDragging(true)
  }

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null
    if (next && event.currentTarget.contains(next)) return
    setDragging(false)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    addFiles(imageFiles(event.dataTransfer?.files))
  }

  const imageError = form.imageError ?? imageFieldError

  return (
    <Stack gap={16}>
      <Stack gap={4}>
        <Textarea
          label={t('paste.text_label')}
          placeholder={t('paste.text_placeholder')}
          autosize
          minRows={6}
          maxRows={16}
          value={form.text}
          disabled={submitting}
          error={textError()}
          onBlur={() => setBlurred(true)}
          onChange={(event) => dispatch({ type: 'set_text', text: event.currentTarget.value })}
        />
        <Group justify="space-between" gap={8} wrap="nowrap">
          {link === 'link' ? (
            <Group gap={4} wrap="nowrap">
              <IconLink size={16} color="var(--mantine-color-gray-6)" />
              <Text size="xs" lh={1.4} c="dimmed">
                {t('paste.link_hint')}
              </Text>
            </Group>
          ) : (
            <span />
          )}
          <Text size="xs" lh={1.4} c="dimmed" ta="right">
            {t('paste.text_counter', { count: form.text.length })}
          </Text>
        </Group>
      </Stack>

      <Stack gap={8}>
        <Text size="sm" fw={500}>
          {t('paste.images_label')}
        </Text>
        <Paper
          data-testid="paste-drop-zone"
          data-dragging={dragging ? 'true' : 'false'}
          radius="md"
          p={16}
          mih={120}
          onDragEnter={onDragOver}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          style={{
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: dragging ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-4)',
            background: dragging ? 'var(--mantine-color-blue-0)' : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Stack align="center" gap={8}>
            <IconPhotoPlus size={32} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed" ta="center">
              {t('paste.drop_hint')}
            </Text>
            <FileButton
              multiple
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              resetRef={resetFileInput}
              disabled={submitting}
              onChange={(files) => {
                addFiles(imageFiles(files))
                resetFileInput.current?.()
              }}
            >
              {(props) => (
                <Button {...props} variant="default" size="xs" disabled={submitting}>
                  {t('paste.choose_files')}
                </Button>
              )}
            </FileButton>
          </Stack>
        </Paper>
        {imageError && (
          <Text size="sm" c="red">
            {t(imageError)}
          </Text>
        )}
        {form.images.length > 0 && (
          <Group gap={8}>
            {form.images.map((image, index) => (
              <Box key={`${urls[index]}-${index}`} data-testid="paste-thumb" pos="relative" w={THUMB} h={THUMB}>
                <Image
                  src={urls[index]}
                  alt={`${t('desk.fields.image')} ${index + 1}`}
                  w={THUMB}
                  h={THUMB}
                  fit="cover"
                  radius="sm"
                />
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="gray"
                  pos="absolute"
                  top={4}
                  right={4}
                  disabled={submitting}
                  aria-label={t('paste.remove_image', { n: index + 1 })}
                  onClick={() => dispatch({ type: 'remove_image', index })}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Box>
            ))}
          </Group>
        )}
      </Stack>

      <Stack gap={4}>
        <Button fullWidth loading={submitting} disabled={!canSend(form)} onClick={onSubmit}>
          {t('paste.submit')}
        </Button>
        {submitting && (
          <Text size="xs" lh={1.4} c="dimmed" ta="center">
            {t('paste.submitting')}
          </Text>
        )}
      </Stack>
    </Stack>
  )
}

/** The alert a refusal puts over the still-filled form, or null. */
const FormAlert = ({ panel }: { panel: PanelState }) => {
  const { t } = useAppTranslation()
  switch (panel.kind) {
    case 'login_wall':
      return (
        <Alert color="orange" title={t('paste.login_wall_title')}>
          <Text size="sm">{t('paste.login_wall_body')}</Text>
        </Alert>
      )
    case 'page_unreadable':
      return (
        <Alert color="orange">
          <Text size="sm">{t('paste.page_unreadable')}</Text>
        </Alert>
      )
    case 'link_refused':
      return (
        <Alert color="orange">
          <Text size="sm">{t('paste.link_refused')}</Text>
        </Alert>
      )
    case 'link_failed':
      return (
        <Alert color="orange">
          <Text size="sm">{t('paste.link_failed')}</Text>
        </Alert>
      )
    case 'cap_reached':
      return (
        <Alert color="orange" title={t('paste.cap_title')}>
          {/* desk-paste always sends its cap; 40 is its default if it ever does not. */}
          <Text size="sm">{t('paste.cap_body', { cap: panel.cap ?? 40 })}</Text>
        </Alert>
      )
    case 'error':
      return (
        <Alert color="red">
          <Text size="sm">{t(panel.messageKey)}</Text>
        </Alert>
      )
    default:
      return null
  }
}

type CandidateRowProps = { candidate: PasteCandidate; onOpen: (id: number) => void }

/** One candidate the paste made: title, start, Pasted + route badges, Open (D-21). */
const CandidateRow = ({ candidate, onOpen }: CandidateRowProps) => {
  const { t, language } = useAppTranslation()
  const badge = routeBadge(candidate)
  const starts = formatStarts(candidate.starting_date, candidate.starting_time, dateLocaleOf(language))
  const href = `/desk/${candidate.application_id}`
  return (
    <Paper withBorder p={16} radius="sm" data-testid="paste-candidate">
      <Group justify="space-between" align="flex-start" gap={16} wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0 }}>
          {candidate.event_title ? (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {candidate.event_title}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              {t('desk.untitled')}
            </Text>
          )}
          <Text size="xs" lh={1.4} c="dimmed">
            {starts ?? t('desk.no_date')}
          </Text>
          <Group gap={8}>
            <SourceBadge source={candidate.source} beat={null} />
            <Badge size="sm" fz={12} variant="light" color={badge.color}>
              {t(badge.key)}
            </Badge>
          </Group>
        </Stack>
        <Anchor
          size="sm"
          href={href}
          onClick={(event) => {
            event.preventDefault()
            onOpen(candidate.application_id)
          }}
        >
          {t('paste.open_candidate')}
        </Anchor>
      </Group>
    </Paper>
  )
}

type ResultBodyProps = {
  state: ResultState | null
  controls: PasteSessionControls
  onOpen: (id: number) => void
}

/** What the editor made (or is making) of the raw item. */
const ResultBody = ({ state, controls, onOpen }: ResultBodyProps) => {
  const { t } = useAppTranslation()

  if (state === null || state.kind === 'reading') {
    return (
      <Stack align="center" gap={8} py={48}>
        <Loader />
        <Text size="md" fw={600} lh={1.3}>
          {t('paste.reading_title')}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {t('paste.reading_body')}
        </Text>
      </Stack>
    )
  }

  if (state.kind === 'waiting') {
    return (
      <Alert color="gray" title={t('paste.waiting_title')}>
        <Stack gap={8} align="flex-start">
          <Text size="sm">{t('paste.waiting_body')}</Text>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            loading={controls.retrying}
            onClick={() => controls.retry(state.rawItemId)}
          >
            {t('paste.retry')}
          </Button>
        </Stack>
      </Alert>
    )
  }

  if (state.kind === 'not_event') {
    return (
      <Alert color="gray" title={t('paste.not_event_title')}>
        <Stack gap={8} align="flex-start">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {state.reason ? t('paste.not_event_body', { reason: state.reason }) : t('paste.not_event_no_reason')}
          </Text>
          <Button
            variant="light"
            leftSection={<IconArrowBackUp size={16} />}
            loading={controls.restoring}
            onClick={() => controls.restore(state)}
          >
            {t('paste.restore')}
          </Button>
        </Stack>
      </Alert>
    )
  }

  return (
    <Stack gap={8}>
      <Text size="md" fw={600} lh={1.3}>
        {t('paste.done', { count: state.candidates.length })}
      </Text>
      {state.candidates.map((candidate) => (
        <CandidateRow key={candidate.application_id} candidate={candidate} onOpen={onOpen} />
      ))}
    </Stack>
  )
}

type PasteDrawerProps = {
  opened: boolean
  onClose: () => void
  /** The Desk page's paste session (usePasteSession): it outlives the drawer. */
  session: PasteSessionControls
}

/**
 * The paste panel (PASTE-01, UI-SPEC §G, D-16..D-21): a right drawer, 480 px,
 * with one text box (text or a single https link) and an image drop zone
 * (drop, paste anywhere in the open drawer, or choose files). No dropzone
 * package (U-13): native drag events, a paste listener and FileButton.
 *
 * Everything shown from the editor (titles, reasons) is plain Text children.
 */
export const PasteDrawer = ({ opened, onClose, session: controls }: PasteDrawerProps) => {
  const { t } = useAppTranslation()
  const navigate = useNavigate()
  const { session, dispatch } = controls
  const { panel } = session
  const formShown = showsForm(panel) || panel.kind === 'submitting'

  const openCandidate = (id: number) => {
    onClose()
    navigate(`/desk/${id}`)
  }

  // Clipboard images pasted anywhere inside the open drawer are added. When
  // the clipboard also carries text, the text still lands in the text box.
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!formShown || panel.kind === 'submitting') return
    const files = imageFiles(event.clipboardData?.files)
    if (files.length === 0) return
    if (!event.clipboardData.getData('text/plain')) event.preventDefault()
    dispatch({ type: 'add_images', files })
  }

  const pasteAnother = (
    <Button variant="default" onClick={() => dispatch({ type: 'reset' })}>
      {t('paste.another')}
    </Button>
  )

  const body = (): ReactNode => {
    if (formShown) {
      return (
        <>
          <FormAlert panel={panel} />
          <PasteForm session={session} dispatch={dispatch} onSubmit={controls.submit} />
        </>
      )
    }
    if (panel.kind === 'already') {
      return (
        <>
          <Alert color="gray" variant="light" title={t('paste.already_title')}>
            <Text size="sm">{t('paste.already_body')}</Text>
          </Alert>
          <ResultBody state={panel.inner} controls={controls} onOpen={openCandidate} />
          {panel.inner !== null && panel.inner.kind !== 'reading' && pasteAnother}
        </>
      )
    }
    const state = panel as ResultState
    return (
      <>
        <ResultBody state={state} controls={controls} onOpen={openCandidate} />
        {state.kind !== 'reading' && pasteAnother}
      </>
    )
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={480}
      title={
        <Text size="md" fw={600} lh={1.3}>
          {t('paste.title')}
        </Text>
      }
      closeButtonProps={{ 'aria-label': t('common.close') }}
    >
      <Stack gap={16} onPaste={onPaste}>
        {body()}
      </Stack>
    </Drawer>
  )
}
